import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  User,
  MapPin,
  Image,
  Star,
  ArrowLeft,
  Camera,
  Lock,
  Sparkles,
  UserPlus,
  UserMinus,
  UserCheck,
  Users,
  ChevronLeft,
  ChevronRight,
  X,
  Coins,
  Unlock,
  AlertCircle,
  CheckCircle,
  Loader2,
  ShoppingCart,
  Heart
} from "lucide-react";
import BuyCreditsModal from "../../components/BuyCreditsModal";
import TipModal from "../../components/TipModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to send cookies with all requests
axios.defaults.withCredentials = true;

// Helper to store return URL before navigation to login/register
const storeReturnUrl = (url) => {
  sessionStorage.setItem('returnUrl', url);
};

// Helper to get correct image data URI
const getImageSrc = (base64Data) => {
  if (!base64Data) return null;
  if (base64Data.startsWith("data:")) return base64Data;
  
  if (base64Data.startsWith("UklGR")) {
    return `data:image/webp;base64,${base64Data}`;
  } else if (base64Data.startsWith("/9j/")) {
    return `data:image/jpeg;base64,${base64Data}`;
  } else if (base64Data.startsWith("iVBOR")) {
    return `data:image/png;base64,${base64Data}`;
  }
  return `data:image/jpeg;base64,${base64Data}`;
};

// Lazy Media Component - fetches image/video when visible using IntersectionObserver
const LazyMedia = ({ contentId, alt, className, onClick, imageCache, loadImage, loadingImages, mediaType }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const containerRef = useRef(null);
  
  // Use IntersectionObserver to load media when visible
  useEffect(() => {
    if (!contentId || imageCache[contentId]) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadImage(contentId);
          observer.disconnect();
        }
      },
      { rootMargin: "100px" }
    );
    
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    
    return () => observer.disconnect();
  }, [contentId, imageCache, loadImage]);
  
  const cachedMedia = imageCache[contentId];
  const isVideo = mediaType === "video" || cachedMedia?.startsWith("data:video/");
  const mediaSrc = cachedMedia ? (isVideo ? cachedMedia : getImageSrc(cachedMedia)) : null;
  const isLoading = loadingImages[contentId];
  
  return (
    <div ref={containerRef} className="relative w-full h-full">
      {/* Skeleton placeholder */}
      {(!isLoaded || !mediaSrc) && (
        <div className="absolute inset-0 bg-gray-700/50 animate-pulse rounded-xl flex items-center justify-center">
          {isLoading && (
            <div className="w-8 h-8 border-2 border-gray-500 border-t-cyan-400 rounded-full animate-spin" />
          )}
          {!isLoading && !mediaSrc && (
            <Image size={24} className="text-gray-500" />
          )}
        </div>
      )}
      {/* Actual media (image or video) */}
      {mediaSrc && (
        isVideo ? (
          <video
            src={mediaSrc}
            className={`${className} ${isLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
            muted
            loop
            playsInline
            onLoadedData={() => setIsLoaded(true)}
            onMouseOver={(e) => e.target.play()}
            onMouseOut={(e) => { e.target.pause(); e.target.currentTime = 0; }}
          />
        ) : (
          <img
            src={mediaSrc}
            alt={alt}
            loading="lazy"
            decoding="async"
            onLoad={() => setIsLoaded(true)}
            className={`${className} ${isLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
          />
        )
      )}
      {/* Video indicator - positioned top-left */}
      {isVideo && mediaSrc && (
        <div className="absolute top-2 left-2 bg-purple-500/90 px-2 py-0.5 rounded-full text-xs text-white font-medium flex items-center gap-1 z-10 pointer-events-none">
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
          Video
        </div>
      )}
    </div>
  );
};

// Keep LazyImage for backward compatibility (alias to LazyMedia)
const LazyImage = (props) => <LazyMedia {...props} />;

// Profile Image with skeleton
const ProfileImage = ({ src, alt, size = "lg" }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const sizeClasses = {
    sm: "w-16 h-16",
    md: "w-24 h-24",
    lg: "w-32 h-32 sm:w-40 sm:h-40"
  };

  return (
    <div className={`${sizeClasses[size]} rounded-full overflow-hidden border-4 border-gray-800 shadow-xl bg-gradient-to-br from-gray-700 to-gray-800 relative`}>
      {!isLoaded && (
        <div className="absolute inset-0 bg-gray-700 animate-pulse" />
      )}
      {src ? (
        <img
          src={src}
          alt={alt}
          loading="eager"
          onLoad={() => setIsLoaded(true)}
          className={`w-full h-full object-cover ${isLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <User size={size === "lg" ? 48 : 24} className="text-gray-500" />
        </div>
      )}
    </div>
  );
};

const PublicCreatorProfilePage = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  
  // Image cache for lazy loading
  const [imageCache, setImageCache] = useState({});
  const [loadingImages, setLoadingImages] = useState({});
  
  // Auth state
  const [currentUser, setCurrentUser] = useState(null);
  const [unlockedIds, setUnlockedIds] = useState(new Set());
  
  // Unlock modal state
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [selectedUnlockItem, setSelectedUnlockItem] = useState(null);
  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState(null);
  const [unlockSuccess, setUnlockSuccess] = useState(false);
  
  // Buy Credits modal state
  const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);
  
  // Tip modal state
  const [showTipModal, setShowTipModal] = useState(false);
  
  // Content pagination state
  const [contentPage, setContentPage] = useState(0);
  const [contentHasMore, setContentHasMore] = useState(true);
  const [loadingMoreContent, setLoadingMoreContent] = useState(false);
  const contentLoaderRef = useRef(null);
  const CONTENT_PAGE_SIZE = 12;
  
  // Follow state
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [followCounts, setFollowCounts] = useState({ followers: 0, following: 0 });
  const [showFollowerCounts, setShowFollowerCounts] = useState(true);
  
  // Fetch follower visibility setting
  useEffect(() => {
    const fetchFollowerVisibility = async () => {
      try {
        const response = await axios.get(`${API}/public/site-settings/follower-visibility`);
        setShowFollowerCounts(response.data.show_followers !== false);
      } catch (err) {
        // Default to showing if API fails
        setShowFollowerCounts(true);
      }
    };
    fetchFollowerVisibility();
  }, []);
  
  // Get auth headers
  // Configure axios for httpOnly cookies - no auth headers needed
  const getAuthHeaders = () => {
    return {}; // Auth is now via httpOnly cookies
  };
  
  // Fetch current user and unlocked content
  const fetchUserData = useCallback(async () => {
    try {
      // Get user profile from /public/auth/me (cookie will be sent automatically)
      const userResponse = await axios.get(`${API}/public/auth/me`);
      setCurrentUser(userResponse.data);
      
      // Get unlocked content IDs
      const unlockedResponse = await axios.get(`${API}/public/unlocked-content`);
      setUnlockedIds(new Set(unlockedResponse.data.unlocked_content_ids || []));
    } catch (err) {
      // Not authenticated or error - just continue without user data
    }
  }, []);

  // Generate a simple visitor ID for tracking unique visitors
  const getVisitorId = () => {
    let visitorId = localStorage.getItem("visitor_id");
    if (!visitorId) {
      visitorId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem("visitor_id", visitorId);
    }
    return visitorId;
  };

  // Track page view
  const trackPageView = async () => {
    try {
      await axios.post(`${API}/public/track-view`, {
        creator_username: username,
        page_type: "profile",
        visitor_id: getVisitorId()
      });
    } catch (err) {
      // Silently ignore tracking errors
      console.debug("View tracking failed:", err);
    }
  };

  // Fetch follow status and counts for the creator
  const fetchFollowData = useCallback(async (creatorId) => {
    if (!creatorId) return;
    
    try {
      // Fetch follow status (cookie sent automatically)
      const statusResponse = await axios.get(`${API}/public/follow-status/${creatorId}`);
      setIsFollowing(statusResponse.data.following || false);
      
      // Fetch follow counts
      const countsResponse = await axios.get(`${API}/public/follow-counts/${creatorId}`);
      setFollowCounts({
        followers: countsResponse.data.followers_count || 0,
        following: countsResponse.data.following_count || 0
      });
    } catch (err) {
      // Not authenticated or error - continue without follow data
    }
  }, []);

  // Handle follow/unfollow
  const handleFollowToggle = async () => {
    if (!currentUser || !creator?.id) return;
    
    setFollowLoading(true);
    try {
      if (isFollowing) {
        await axios.delete(`${API}/public/follow/${creator.id}`);
        setIsFollowing(false);
        setFollowCounts(prev => ({ ...prev, followers: Math.max(0, prev.followers - 1) }));
      } else {
        await axios.post(`${API}/public/follow/${creator.id}`, {});
        setIsFollowing(true);
        setFollowCounts(prev => ({ ...prev, followers: prev.followers + 1 }));
      }
    } catch (err) {
    } finally {
      setFollowLoading(false);
    }
  };

  // Track which images are already being fetched to avoid duplicate requests
  const fetchingRef = useRef(new Set());

  // Lazy load a single image - using refs to avoid stale closure issues
  const loadImage = useCallback((contentId) => {
    if (!contentId) return;
    
    // Check if already fetching
    if (fetchingRef.current.has(contentId)) return;
    
    // Check if already cached
    setImageCache(prev => {
      if (prev[contentId]) return prev; // Already cached, do nothing
      
      // Mark as fetching
      fetchingRef.current.add(contentId);
      setLoadingImages(loadingPrev => ({ ...loadingPrev, [contentId]: true }));
      
      // Start the fetch asynchronously - cookies sent automatically for auth
      axios.get(`${API}/public/content/${contentId}/image`)
        .then(response => {
          if (response.data.image_data) {
            setImageCache(cachePrev => ({ ...cachePrev, [contentId]: response.data.image_data }));
          }
        })
        .catch(err => {
          // 403 means premium content not unlocked - don't log as error
          if (err.response?.status !== 403) {
          }
        })
        .finally(() => {
          fetchingRef.current.delete(contentId);
          setLoadingImages(loadingPrev => {
            const { [contentId]: _, ...rest } = loadingPrev;
            return rest;
          });
        });
      
      return prev;
    });
  }, []);

  // Preload adjacent images for smooth carousel navigation
  const preloadAdjacentImages = useCallback((currentIndex, content) => {
    if (!content || content.length === 0) return;
    
    const indicesToLoad = [
      currentIndex,
      (currentIndex + 1) % content.length,
      (currentIndex - 1 + content.length) % content.length
    ];
    
    indicesToLoad.forEach(idx => {
      const item = content[idx];
      if (item?.id) {
        loadImage(item.id);
      }
    });
  }, [loadImage]);

  useEffect(() => {
    fetchCreator();
    fetchUserData();
  }, [username, fetchUserData]);

  // Track view after creator is loaded successfully
  useEffect(() => {
    if (creator && !error) {
      trackPageView();
      // Fetch follow data if user is logged in
      if (currentUser && creator.id) {
        fetchFollowData(creator.id);
      }
    }
  }, [creator, currentUser, fetchFollowData]);

  // Infinite scroll observer for creator content
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && contentHasMore && !loadingMoreContent) {
          loadMoreCreatorContent();
        }
      },
      { threshold: 0.1 }
    );
    
    if (contentLoaderRef.current) {
      observer.observe(contentLoaderRef.current);
    }
    
    return () => observer.disconnect();
  }, [contentHasMore, loadingMoreContent]);

  const fetchCreator = async (reset = true) => {
    if (!reset) {
      setLoadingMoreContent(true);
    }
    
    try {
      // Include auth header to get unlocked status for each item
      const skip = reset ? 0 : (creator?.content?.length || 0);
      const response = await axios.get(
        `${API}/public/creator/${username}?content_limit=${CONTENT_PAGE_SIZE}&content_skip=${skip}`, 
        { headers: getAuthHeaders() }
      );
      
      if (reset) {
        setCreator(response.data);
        setContentPage(1);
      } else {
        // Append new content to existing
        setCreator(prev => ({
          ...prev,
          content: [...(prev?.content || []), ...(response.data.content || [])],
          content_count: (prev?.content?.length || 0) + (response.data.content?.length || 0)
        }));
        setContentPage(prev => prev + 1);
      }
      setContentHasMore(response.data.content_has_more || false);
    } catch (err) {
      if (err.response?.status === 404) {
        setError("This creator profile doesn't exist or is not available.");
      } else {
        setError("Failed to load profile. Please try again later.");
      }
    } finally {
      setLoading(false);
      setLoadingMoreContent(false);
    }
  };
  
  const loadMoreCreatorContent = () => {
    if (!loadingMoreContent && contentHasMore) {
      fetchCreator(false);
    }
  };

  // Handle unlock content
  const handleUnlock = async () => {
    if (!selectedUnlockItem) return;
    
    setUnlocking(true);
    setUnlockError(null);
    
    try {
      const response = await axios.post(
        `${API}/public/unlock-content/${selectedUnlockItem.id}`,
        {},
        { headers: getAuthHeaders() }
      );
      
      // Update local state
      setUnlockedIds(prev => new Set([...prev, selectedUnlockItem.id]));
      
      // Update user credits
      if (response.data.new_balance !== undefined) {
        setCurrentUser(prev => prev ? { ...prev, credits: response.data.new_balance } : prev);
      }
      
      // Show success and load image
      setUnlockSuccess(true);
      loadImage(selectedUnlockItem.id);
      
      // Close modal after a short delay
      setTimeout(() => {
        setShowUnlockModal(false);
        setSelectedUnlockItem(null);
        setUnlockSuccess(false);
      }, 1500);
      
    } catch (err) {
      setUnlockError(err.response?.data?.detail || "Failed to unlock content");
    } finally {
      setUnlocking(false);
    }
  };
  
  // Open unlock modal
  const openUnlockModal = (item) => {
    if (!currentUser) {
      // Store return URL and redirect to login if not authenticated
      sessionStorage.setItem('returnUrl', `/u/${username}`);
      navigate("/login", { state: { from: `/u/${username}` } });
      return;
    }
    
    setSelectedUnlockItem(item);
    setUnlockError(null);
    setUnlockSuccess(false);
    setShowUnlockModal(true);
  };
  
  // Check if content is accessible (free or unlocked)
  const isContentAccessible = (item) => {
    if (!item) return false;
    // Free content (category is not premium) or no cost set
    if (item.category !== "premium") return true;
    // Premium but cost is 0 or not set
    if (!item.cost || item.cost === 0) return true;
    // Check if unlocked via API response or local state
    return item.is_unlocked === true || unlockedIds.has(item.id);
  };

  // Lightbox navigation handlers - only show accessible content
  const content = creator?.content || [];
  // Filter to only accessible (free or unlocked) content for the lightbox
  const accessibleContent = content.filter(item => isContentAccessible(item));
  const totalImages = accessibleContent.length;

  // Helper to find accessible content index from original content index
  const getAccessibleIndex = useCallback((originalIndex) => {
    const item = content[originalIndex];
    if (!item) return -1;
    return accessibleContent.findIndex(c => c.id === item.id);
  }, [content, accessibleContent]);

  const goToPrevious = useCallback(() => {
    if (selectedImageIndex !== null && totalImages > 0) {
      const newIndex = selectedImageIndex === 0 ? totalImages - 1 : selectedImageIndex - 1;
      setSelectedImageIndex(newIndex);
      preloadAdjacentImages(newIndex, accessibleContent);
    }
  }, [selectedImageIndex, totalImages, accessibleContent, preloadAdjacentImages]);

  const goToNext = useCallback(() => {
    if (selectedImageIndex !== null && totalImages > 0) {
      const newIndex = selectedImageIndex === totalImages - 1 ? 0 : selectedImageIndex + 1;
      setSelectedImageIndex(newIndex);
      preloadAdjacentImages(newIndex, accessibleContent);
    }
  }, [selectedImageIndex, totalImages, accessibleContent, preloadAdjacentImages]);

  const closeLightbox = useCallback(() => {
    setSelectedImageIndex(null);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedImageIndex === null) return;
      
      switch (e.key) {
        case "ArrowLeft":
          e.preventDefault();
          goToPrevious();
          break;
        case "ArrowRight":
          e.preventDefault();
          goToNext();
          break;
        case "Escape":
          e.preventDefault();
          closeLightbox();
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImageIndex, goToPrevious, goToNext, closeLightbox]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (selectedImageIndex !== null) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [selectedImageIndex]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#00A8E8] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center p-4">
        <div className="text-center">
          <User size={64} className="mx-auto text-gray-600 mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Profile Not Found</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#00A8E8] text-white rounded-lg hover:bg-[#0090c5] transition-colors"
          >
            <ArrowLeft size={18} />
            Go to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900" data-testid="public-creator-profile">
      {/* Header */}
      <header className="bg-gray-800/50 border-b border-gray-700/50 sticky top-0 z-40 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <h1 className="text-2xl font-bold" style={{ fontFamily: "'Montserrat', sans-serif", letterSpacing: '-0.02em' }}>
                <span className="text-white">Hot</span>
                <span className="text-[#00A8E8]">Peek</span>
              </h1>
            </Link>
            {currentUser ? (
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setShowBuyCreditsModal(true)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-amber-500/20 to-amber-600/20 border border-amber-500/30 text-amber-400 rounded-lg hover:border-amber-500/50 transition-colors text-sm font-medium"
                  data-testid="buy-credits-header-btn"
                >
                  <ShoppingCart className="w-3.5 h-3.5" />
                  Buy
                </button>
                <div className="flex items-center gap-2 text-amber-400">
                  <Coins className="w-4 h-4" />
                  <span className="font-medium">{currentUser.credits || 0}</span>
                </div>
                <Link
                  to="/app/profile"
                  className="px-4 py-2 text-sm text-gray-300 hover:text-white border border-gray-700 rounded-lg hover:border-gray-600 transition-colors"
                >
                  {currentUser.name || "My Profile"}
                </Link>
              </div>
            ) : (
              <Link
                to="/login"
                state={{ from: `/u/${username}` }}
                onClick={() => storeReturnUrl(`/u/${username}`)}
                className="px-4 py-2 text-sm text-gray-400 hover:text-white border border-gray-700 rounded-lg hover:border-gray-600 transition-colors"
              >
                Sign In
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-800/50 rounded-2xl border border-gray-700/50 overflow-hidden mb-8"
        >
          {/* Header Image */}
          <div className="h-48 sm:h-64 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 relative">
            {creator.header_image && (
              <img
                src={getImageSrc(creator.header_image)}
                alt="Header"
                loading="eager"
                className="w-full h-full object-cover"
              />
            )}
          </div>

          {/* Profile Info */}
          <div className="px-6 pb-6">
            <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-16 sm:-mt-20">
              {/* Avatar */}
              <div className="relative w-28 h-28 sm:w-36 sm:h-36 rounded-2xl overflow-hidden border-4 border-gray-800 bg-gray-700 shadow-xl">
                {(creator.avatar || creator.profile_image) ? (
                  <img
                    src={getImageSrc(creator.avatar || creator.profile_image)}
                    alt={creator.name}
                    loading="eager"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User size={48} className="text-gray-500" />
                  </div>
                )}
              </div>

              {/* Name & Info */}
              <div className="flex-1 pt-4 sm:pt-0 sm:pb-2">
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-3xl sm:text-4xl font-bold text-white">{creator.name}</h1>
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                    Creator
                  </span>
                </div>
                <p className="text-gray-400 mt-1">@{creator.username}</p>
                {creator.location && (
                  <p className="text-gray-500 text-sm mt-1 flex items-center gap-1">
                    <MapPin size={14} />
                    {creator.location}
                  </p>
                )}
                
                {/* Follower Stats & Follow Button */}
                {currentUser && (
                  <div className="flex items-center gap-4 mt-4">
                    {/* Follower counts - only show if enabled */}
                    {showFollowerCounts && (
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-gray-400">
                          <span className="text-white font-semibold">{followCounts.followers}</span> followers
                        </span>
                        <span className="text-gray-400">
                          <span className="text-white font-semibold">{followCounts.following}</span> following
                        </span>
                      </div>
                    )}
                    
                    {/* Follow Button - don't show if viewing own profile */}
                    {currentUser.id !== creator.id && (
                      <>
                        <button
                          onClick={handleFollowToggle}
                          disabled={followLoading}
                          className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                            isFollowing
                              ? "bg-gray-600 text-white hover:bg-red-500"
                              : "bg-gradient-to-r from-cyan-500 to-purple-600 text-white hover:opacity-90"
                          }`}
                          data-testid="follow-button"
                        >
                          {followLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : isFollowing ? (
                            <>
                              <UserCheck className="w-4 h-4" />
                              Following
                            </>
                          ) : (
                            <>
                              <UserPlus className="w-4 h-4" />
                              Follow
                            </>
                          )}
                        </button>
                        
                        {/* Tip Button - only for fans */}
                        {currentUser.role === "fan" && (
                          <button
                            onClick={() => setShowTipModal(true)}
                            className="px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 bg-gradient-to-r from-pink-500 to-rose-500 text-white hover:opacity-90"
                            data-testid="tip-button"
                          >
                            <Heart className="w-4 h-4" />
                            Send Tip
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Bio */}
            {(creator.bio || creator.headline) && (
              <p className="text-gray-300 mt-6 text-lg leading-relaxed">
                {creator.bio || creator.headline}
              </p>
            )}
          </div>
        </motion.div>

        {/* Content Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          {/* Credits Banner for logged-in users */}
          {currentUser && (
            <div className="mb-6 bg-gradient-to-r from-amber-500/10 to-purple-500/10 border border-amber-500/20 rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <Coins className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-white font-medium">Your Credit Balance</p>
                  <p className="text-gray-400 text-sm">Use credits to unlock premium content</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-amber-400">{currentUser.credits || 0}</p>
                <p className="text-gray-500 text-xs">credits</p>
              </div>
            </div>
          )}
          
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <Image size={22} className="text-purple-400" />
              Content
              <span className="text-gray-500 font-normal">
                ({creator.free_content_count || creator.content_count} free
                {creator.premium_content_count > 0 && `, ${creator.premium_content_count} premium`})
              </span>
            </h2>
            {!currentUser && creator.premium_content_count > 0 && (
              <Link 
                to="/login"
                state={{ from: `/u/${username}` }}
                onClick={() => storeReturnUrl(`/u/${username}`)}
                className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-purple-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity flex items-center gap-2"
              >
                <UserPlus size={16} />
                Sign in to unlock
              </Link>
            )}
          </div>

          {creator.content && creator.content.length > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {creator.content.map((item, index) => {
                  const isPremium = item.category === "premium" && item.cost > 0;
                  const isAccessible = isContentAccessible(item);
                  
                  return (
                    <motion.div
                      key={item.id || index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: Math.min(index * 0.03, 0.3) }}
                      className={`aspect-square rounded-xl overflow-hidden bg-gray-800/50 border cursor-pointer transition-colors group relative ${
                        isPremium && !isAccessible
                          ? "border-amber-500/30 hover:border-amber-500/50"
                          : "border-gray-700/50 hover:border-cyan-500/50"
                      }`}
                      data-testid={`content-item-${index}`}
                      onClick={() => {
                        if (isPremium && !isAccessible) {
                          // Open unlock modal for locked premium content
                          openUnlockModal(item);
                        } else {
                          // Show lightbox for accessible content - use accessibleContent index
                          const accessibleIdx = getAccessibleIndex(index);
                          if (accessibleIdx >= 0) {
                            preloadAdjacentImages(accessibleIdx, accessibleContent);
                            setSelectedImageIndex(accessibleIdx);
                          }
                        }
                      }}
                    >
                      {/* Show image/video - blurred if locked, clear if accessible */}
                      <div className="relative w-full h-full pointer-events-none">
                        <LazyMedia
                          contentId={item.id}
                          alt={item.title || `Content ${index + 1}`}
                          className={`w-full h-full object-cover transition-all duration-300 ${
                            isAccessible 
                              ? "group-hover:scale-105" 
                              : "blur-xl scale-110"
                          }`}
                          imageCache={imageCache}
                          loadImage={loadImage}
                          loadingImages={loadingImages}
                          mediaType={item.media_type}
                        />
                        
                        {/* Overlay for locked content */}
                        {!isAccessible && (
                          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40">
                            <div className="w-14 h-14 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center mb-2 border border-amber-500/30">
                              <Lock className="w-7 h-7 text-amber-400" />
                            </div>
                            <div className="flex items-center gap-1.5 text-amber-400 font-bold text-lg">
                              <Coins className="w-5 h-5" />
                              <span>{item.cost}</span>
                            </div>
                            <p className="text-white/70 text-xs mt-1.5 font-medium">Click to unlock</p>
                          </div>
                        )}
                      </div>
                      
                      {/* Premium badge */}
                      {isPremium && (
                        <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
                          isAccessible
                            ? "bg-green-500/90 text-white"
                            : "bg-amber-500/90 text-black"
                        }`}>
                          {isAccessible ? (
                            <>
                              <Unlock className="w-3 h-3" />
                              Unlocked
                            </>
                          ) : (
                            <>
                              <Lock className="w-3 h-3" />
                              Premium
                            </>
                          )}
                        </div>
                      )}
                      
                      {/* Title overlay - always visible if title exists and accessible */}
                      {item.title && isAccessible && (
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 pt-6 pointer-events-none z-10">
                          <p className="text-white text-xs font-medium truncate">{item.title}</p>
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
              
              {/* Infinite scroll loader / Load More button */}
              <div ref={contentLoaderRef} className="flex flex-col items-center gap-4 py-6">
                {loadingMoreContent && (
                  <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
                )}
                {contentHasMore && !loadingMoreContent && creator.content.length >= CONTENT_PAGE_SIZE && (
                  <button
                    onClick={loadMoreCreatorContent}
                    className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-medium rounded-lg hover:opacity-90 transition-opacity"
                    data-testid="load-more-content-btn"
                  >
                    Load More Content
                  </button>
                )}
                {!contentHasMore && creator.content.length > CONTENT_PAGE_SIZE && (
                  <p className="text-gray-500 text-sm">All content loaded</p>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-16 bg-gray-800/30 rounded-2xl border border-gray-700/50">
              <Camera size={56} className="mx-auto text-gray-600 mb-4" />
              <p className="text-gray-500 text-lg">No content yet</p>
              <p className="text-gray-600 text-sm mt-2">Check back soon for new posts!</p>
            </div>
          )}
        </motion.div>

        {/* Unlock CTA Card - Only show for non-logged-in users */}
        {!currentUser && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-10"
          >
            <div className="relative overflow-hidden bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-2xl border border-gray-700/50 p-8 text-center">
              {/* Background decoration */}
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-24 -right-24 w-48 h-48 bg-gradient-to-br from-[#00A8E8]/20 to-[#D000FF]/20 rounded-full blur-3xl" />
                <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-gradient-to-br from-[#D000FF]/20 to-[#00A8E8]/20 rounded-full blur-3xl" />
              </div>
              
              <div className="relative z-10">
                {/* Lock Icon */}
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#00A8E8]/20 to-[#D000FF]/20 flex items-center justify-center border border-[#00A8E8]/30">
                  <Lock size={28} className="text-[#00A8E8]" />
                </div>
                
                {/* Headline */}
                <h3 className="text-2xl font-bold text-white mb-2">
                  Want to see more?
                </h3>
                <p className="text-gray-400 mb-6 max-w-md mx-auto">
                  Register as a Fan to unlock exclusive content from <span className="text-white font-medium">{creator.name}</span> and other creators on HotPeek.
                </p>
                
                {/* Features */}
                <div className="flex flex-wrap justify-center gap-4 mb-6">
                  <div className="flex items-center gap-2 text-sm text-gray-300">
                    <Sparkles size={16} className="text-amber-400" />
                    <span>Exclusive Content</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-300">
                    <UserPlus size={16} className="text-green-400" />
                    <span>Free to Join</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-300">
                    <Star size={16} className="text-purple-400" />
                    <span>Support Creators</span>
                  </div>
                </div>
                
                {/* CTA Button */}
                <button
                  onClick={() => {
                    storeReturnUrl(`/u/${username}`);
                    navigate("/register", { state: { from: `/u/${username}` } });
                  }}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#00A8E8] to-[#D000FF] text-white font-semibold rounded-xl hover:opacity-90 transition-opacity shadow-lg shadow-[#00A8E8]/25 text-lg"
                  data-testid="unlock-cta-btn"
                >
                  <Lock size={20} />
                  Unlock Now
                </button>
                
                <p className="text-gray-500 text-sm mt-4">
                  Already have an account? <Link to="/login" state={{ from: `/u/${username}` }} onClick={() => storeReturnUrl(`/u/${username}`)} className="text-[#00A8E8] hover:underline">Sign in</Link>
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </main>

      {/* Lightbox Carousel - Only accessible (free/unlocked) content */}
      <AnimatePresence>
        {selectedImageIndex !== null && accessibleContent.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex flex-col"
            onClick={closeLightbox}
            data-testid="lightbox-carousel"
          >
            {/* Top Bar */}
            <div className="flex items-center justify-between p-4 text-white">
              <div className="flex items-center gap-4">
                <span className="text-lg font-medium">
                  {selectedImageIndex + 1} / {totalImages}
                </span>
                {accessibleContent[selectedImageIndex]?.title && (
                  <span className="text-gray-400 hidden sm:inline">
                    {accessibleContent[selectedImageIndex].title}
                  </span>
                )}
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  closeLightbox();
                }}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
                data-testid="lightbox-close"
              >
                <X size={24} />
              </button>
            </div>

            {/* Main Image Area */}
            <div className="flex-1 flex items-center justify-center relative px-4 sm:px-16">
              {/* Previous Button */}
              {totalImages > 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    goToPrevious();
                  }}
                  className="absolute left-2 sm:left-4 p-2 sm:p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
                  data-testid="lightbox-prev"
                >
                  <ChevronLeft size={28} className="text-white" />
                </button>
              )}

              {/* Image/Video */}
              <motion.div
                key={selectedImageIndex}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="flex-1 flex items-center justify-center px-4"
                onClick={(e) => e.stopPropagation()}
              >
                {(() => {
                  const currentItem = accessibleContent[selectedImageIndex];
                  const cachedMedia = currentItem?.id ? imageCache[currentItem.id] : null;
                  const isVideo = currentItem?.media_type === "video" || cachedMedia?.startsWith("data:video/");
                  
                  if (cachedMedia) {
                    if (isVideo) {
                      return (
                        <video
                          src={cachedMedia}
                          className="max-w-[85vw] max-h-[70vh] object-contain rounded-lg shadow-2xl"
                          style={{ minWidth: '200px', minHeight: '200px' }}
                          controls
                          autoPlay
                          playsInline
                        />
                      );
                    } else {
                      return (
                        <img
                          src={getImageSrc(cachedMedia)}
                          alt={currentItem?.title || `Image ${selectedImageIndex + 1}`}
                          loading="eager"
                          decoding="async"
                          className="max-w-[85vw] max-h-[70vh] object-contain rounded-lg shadow-2xl"
                          style={{ minWidth: '200px', minHeight: '200px' }}
                          draggable={false}
                        />
                      );
                    }
                  } else {
                    return (
                      <div className="w-64 h-64 flex items-center justify-center bg-gray-800 rounded-lg">
                        <div className="w-12 h-12 border-4 border-gray-600 border-t-cyan-400 rounded-full animate-spin" />
                      </div>
                    );
                  }
                })()}
              </motion.div>

              {/* Next Button */}
              {totalImages > 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    goToNext();
                  }}
                  className="absolute right-2 sm:right-4 p-2 sm:p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
                  data-testid="lightbox-next"
                >
                  <ChevronRight size={28} className="text-white" />
                </button>
              )}
            </div>

            {/* Thumbnail Strip - Only accessible content */}
            {totalImages > 1 && (
              <div className="p-4 overflow-x-auto">
                <div className="flex gap-2 justify-center">
                  {accessibleContent.map((item, index) => {
                    const isVideo = item.media_type === "video" || imageCache[item.id]?.startsWith("data:video/");
                    return (
                      <button
                        key={item.id || index}
                        onClick={(e) => {
                          e.stopPropagation();
                          // Preload when clicking thumbnail
                          preloadAdjacentImages(index, accessibleContent);
                          setSelectedImageIndex(index);
                        }}
                        className={`relative w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all ${
                          index === selectedImageIndex
                            ? "border-[#00A8E8] ring-2 ring-[#00A8E8]/50"
                            : "border-transparent opacity-50 hover:opacity-100"
                        }`}
                        data-testid={`thumbnail-${index}`}
                      >
                        {imageCache[item.id] ? (
                          isVideo ? (
                            <video
                              src={imageCache[item.id]}
                              className="w-full h-full object-cover"
                              muted
                              playsInline
                            />
                          ) : (
                            <img
                              src={getImageSrc(imageCache[item.id])}
                              alt={`Thumbnail ${index + 1}`}
                              loading="lazy"
                              decoding="async"
                              className="w-full h-full object-cover"
                              draggable={false}
                            />
                          )
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gray-700">
                            {loadingImages[item.id] ? (
                              <div className="w-4 h-4 border-2 border-gray-500 border-t-cyan-400 rounded-full animate-spin" />
                            ) : (
                              <Image size={16} className="text-gray-500" />
                            )}
                          </div>
                        )}
                        {/* Video indicator on thumbnail */}
                        {isVideo && (
                          <div className="absolute bottom-1 right-1 bg-black/70 p-1 rounded-full">
                            <svg className="w-2 h-2 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Keyboard Hint */}
            <div className="hidden sm:flex justify-center pb-4 text-gray-500 text-sm gap-4">
              <span>← → Navigate</span>
              <span>ESC Close</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Unlock Modal */}
      <AnimatePresence>
        {showUnlockModal && selectedUnlockItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowUnlockModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-slate-800 rounded-2xl w-full max-w-md border border-white/10 shadow-2xl overflow-hidden"
            >
              {/* Preview Image (blurred) */}
              <div className="relative h-48 bg-gradient-to-br from-amber-900/30 to-purple-900/30 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-black/30 flex items-center justify-center mx-auto mb-3">
                    <Lock className="w-10 h-10 text-amber-400" />
                  </div>
                  <p className="text-white font-semibold">{selectedUnlockItem.title || "Premium Content"}</p>
                </div>
              </div>
              
              {/* Modal Content */}
              <div className="p-6">
                {unlockSuccess ? (
                  <div className="text-center py-4">
                    <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">Unlocked!</h3>
                    <p className="text-gray-400">This content is now available to view.</p>
                  </div>
                ) : (
                  <>
                    <h3 className="text-xl font-bold text-white mb-4 text-center">Unlock Premium Content</h3>
                    
                    {/* Cost and Balance */}
                    <div className="bg-white/5 rounded-xl p-4 mb-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-gray-400">Content Cost</span>
                        <div className="flex items-center gap-1 text-amber-400 font-bold text-lg">
                          <Coins className="w-5 h-5" />
                          {selectedUnlockItem.cost}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">Your Balance</span>
                        <div className={`flex items-center gap-1 font-bold text-lg ${
                          (currentUser?.credits || 0) >= selectedUnlockItem.cost ? "text-green-400" : "text-red-400"
                        }`}>
                          <Coins className="w-5 h-5" />
                          {currentUser?.credits || 0}
                        </div>
                      </div>
                      {(currentUser?.credits || 0) >= selectedUnlockItem.cost && (
                        <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
                          <span className="text-gray-400">After Unlock</span>
                          <div className="flex items-center gap-1 text-gray-300 font-bold text-lg">
                            <Coins className="w-5 h-5" />
                            {(currentUser?.credits || 0) - selectedUnlockItem.cost}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Error Message */}
                    {unlockError && (
                      <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4 flex items-center gap-2 text-red-400">
                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                        <span className="text-sm">{unlockError}</span>
                      </div>
                    )}
                    
                    {/* Insufficient Balance Warning */}
                    {(currentUser?.credits || 0) < selectedUnlockItem.cost && (
                      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 mb-4 flex items-center gap-2 text-amber-400">
                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                        <span className="text-sm">You need {selectedUnlockItem.cost - (currentUser?.credits || 0)} more credits to unlock this content.</span>
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowUnlockModal(false)}
                        className="flex-1 py-3 border border-white/20 text-gray-300 font-medium rounded-lg hover:bg-white/5 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleUnlock}
                        disabled={unlocking || (currentUser?.credits || 0) < selectedUnlockItem.cost}
                        className="flex-1 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {unlocking ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <Unlock className="w-5 h-5" />
                            Unlock for {selectedUnlockItem.cost}
                          </>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Buy Credits Modal */}
      <BuyCreditsModal
        isOpen={showBuyCreditsModal}
        onClose={() => setShowBuyCreditsModal(false)}
        currentCredits={currentUser?.credits || 0}
        onSuccess={() => {
          // Refresh user data
          fetchUserData();
          setShowBuyCreditsModal(false);
        }}
      />

      {/* Tip Modal */}
      <TipModal
        isOpen={showTipModal}
        onClose={() => setShowTipModal(false)}
        creator={creator}
        userCredits={currentUser?.credits || 0}
        onSuccess={(data) => {
          // Update local user credits
          if (currentUser) {
            setCurrentUser({ ...currentUser, credits: data.new_balance });
          }
        }}
      />

      {/* Footer */}
      <footer className="bg-gray-800/30 border-t border-gray-700/50 mt-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2">
              <span className="text-xl font-bold" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                <span className="text-white">Hot</span>
                <span className="text-[#00A8E8]">Peek</span>
              </span>
            </Link>
            <p className="text-gray-500 text-sm">© 2025 HotPeek. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicCreatorProfilePage;
